import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import API_BASE from '../../../apiConfig';
import { apiGet } from '../../../services_1/apiClient';
import './SignupWindow.css';

const ROLE_OPTIONS = [
  { value: 'demand_planner', label: 'Demand Planner' },
  { value: 'brand_manager', label: 'Brand Manager' },
  { value: 'sales_manager', label: 'Sales Manager' },
  { value: 'sc_analyst', label: 'SC Analyst' },
  { value: 'executive', label: 'Executive' },
];

const LOCKED_FIELD_CONFIG = {
  brand_manager: {
    field: 'locked_brand',
    label: 'Locked Brand',
    placeholder: 'Select a brand',
    optionsKey: 'brands',
    helper: 'Choose the brand this account should be locked to.',
  },
  sales_manager: {
    field: 'locked_channel',
    label: 'Locked Channel',
    placeholder: 'Select a channel',
    optionsKey: 'channels',
    helper: 'Choose the channel this account should be locked to.',
  },
};

const SignupWindow = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    username: '',
    email: '',
    department: '',
    role_name: 'demand_planner',
    locked_brand: '',
    locked_channel: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [dimensionOptions, setDimensionOptions] = useState({
    brands: [],
    channels: [],
  });
  const [dimensionLoading, setDimensionLoading] = useState(true);

  React.useEffect(() => {
    let mounted = true;

    const loadDimensionOptions = async () => {
      setDimensionLoading(true);
      try {
        const [brandsRes, channelsRes] = await Promise.all([
          apiGet('/api/dimension-values/brand?limit=200').catch(() => ({ values: [] })),
          apiGet('/api/dimension-values/channel?limit=200').catch(() => ({ values: [] })),
        ]);

        if (!mounted) return;
        setDimensionOptions({
          brands: brandsRes.values || [],
          channels: channelsRes.values || [],
        });
      } finally {
        if (mounted) setDimensionLoading(false);
      }
    };

    loadDimensionOptions();

    return () => {
      mounted = false;
    };
  }, []);

  const validateForm = () => {
    const errors = {};

    if (!formData.full_name.trim()) errors.full_name = 'Full name is required';
    if (!formData.username.trim()) errors.username = 'Username is required';
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    if (!formData.department.trim()) errors.department = 'Department is required';

    const lockedFieldConfig = LOCKED_FIELD_CONFIG[formData.role_name];
    if (lockedFieldConfig) {
      const selectedValue = (formData[lockedFieldConfig.field] || '').trim();
      if (!selectedValue) {
        errors[lockedFieldConfig.field] = `${lockedFieldConfig.label} is required`;
      } else if (
        !dimensionLoading &&
        dimensionOptions[lockedFieldConfig.optionsKey].length > 0 &&
        !dimensionOptions[lockedFieldConfig.optionsKey].includes(selectedValue)
      ) {
        errors[lockedFieldConfig.field] = `Please select a valid ${lockedFieldConfig.label.toLowerCase()}`;
      }
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    } else if (!/[A-Z]/.test(formData.password)) {
      errors.password = 'Password must contain an uppercase letter';
    } else if (!/[a-z]/.test(formData.password)) {
      errors.password = 'Password must contain a lowercase letter';
    } else if (!/[0-9]/.test(formData.password)) {
      errors.password = 'Password must contain a number';
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleRoleChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => {
      const next = {
        ...prev,
        role_name: value,
      };

      if (value === 'brand_manager') {
        next.locked_channel = '';
      } else if (value === 'sales_manager') {
        next.locked_brand = '';
      } else {
        next.locked_brand = '';
        next.locked_channel = '';
      }

      return next;
    });
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.locked_brand;
      delete next.locked_channel;
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: formData.full_name,
          username: formData.username,
          email: formData.email,
          department: formData.department,
          role_name: formData.role_name,
          locked_brand: formData.locked_brand,
          locked_channel: formData.locked_channel,
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }

      setSuccess(data.message || 'Registration submitted successfully!');
      setFormData({
        full_name: '',
        username: '',
        email: '',
        department: '',
        role_name: 'demand_planner',
        locked_brand: '',
        locked_channel: '',
        password: '',
        confirmPassword: '',
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const lockedFieldConfig = LOCKED_FIELD_CONFIG[formData.role_name];
  const lockedFieldOptions = lockedFieldConfig ? (dimensionOptions[lockedFieldConfig.optionsKey] || []) : [];

  return (
    <div className="login-wrapper">
      <div className="login-split-container">
        {/* Left Side: Branding */}
        <div className="login-hero-section">
          <div className="login-hero-content">
            <div className="logo-badge">ForeC.AI</div>
            <h2>Next Generation <br/>Supply Chain Intelligence</h2>
            <p>Empowering your organization with prescriptive analytics, scenario modeling, and predictive insights.</p>
            
            <div className="hero-floating-elements">
              <div className="float-card card-1">
                <span>Forecast Accuracy</span>
                <strong>+24%</strong>
              </div>
              <div className="float-card card-2">
                <span>Optimization</span>
                <strong>Real-time</strong>
              </div>
            </div>
          </div>
          <div className="hero-overlay"></div>
        </div>

        {/* Right Side: Signup Form */}
        <div className="login-form-section">
          <div className="login-form-container" style={{ maxWidth: '420px' }}>
            <div className="login-header">
              <h1 className="login-title">Create Account</h1>
              <p className="login-subtitle">Register for the Organization Dashboard</p>
            </div>
            
            {error && (
              <div className="login-error-alert animate-shake">
                <span className="alert-icon">!</span>
                <span className="alert-message">{error}</span>
              </div>
            )}

            {success && (
              <div className="signup-success-alert">
                <span className="alert-icon">✓</span>
                <span className="alert-message">{success}</span>
              </div>
            )}

            {!success && (
              <form className="login-form" onSubmit={handleSubmit}>
                <div className="input-group">
                  <label htmlFor="full_name">Full Name</label>
                  <div className="input-with-icon">
                    <span className="input-icon">👤</span>
                    <input
                      id="full_name"
                      name="full_name"
                      type="text"
                      value={formData.full_name}
                      onChange={handleChange}
                      placeholder="e.g. John Doe"
                      className={fieldErrors.full_name ? 'input-error' : ''}
                      required
                    />
                  </div>
                  {fieldErrors.full_name && <span className="field-error">{fieldErrors.full_name}</span>}
                </div>

                <div className="input-group">
                  <label htmlFor="username">Username</label>
                  <div className="input-with-icon">
                    <span className="input-icon">@</span>
                    <input
                      id="username"
                      name="username"
                      type="text"
                      value={formData.username}
                      onChange={handleChange}
                      placeholder="e.g. johndoe"
                      className={fieldErrors.username ? 'input-error' : ''}
                      required
                    />
                  </div>
                  {fieldErrors.username && <span className="field-error">{fieldErrors.username}</span>}
                </div>

                <div className="input-group">
                  <label htmlFor="email">Email Address</label>
                  <div className="input-with-icon">
                    <span className="input-icon">✉</span>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="e.g. john@company.com"
                      className={fieldErrors.email ? 'input-error' : ''}
                      required
                    />
                  </div>
                  {fieldErrors.email && <span className="field-error">{fieldErrors.email}</span>}
                </div>

                <div className="input-group">
                  <label htmlFor="department">Department</label>
                  <div className="input-with-icon">
                    <span className="input-icon">🏢</span>
                    <input
                      id="department"
                      name="department"
                      type="text"
                      value={formData.department}
                      onChange={handleChange}
                      placeholder="e.g. Supply Chain"
                      className={fieldErrors.department ? 'input-error' : ''}
                      required
                    />
                  </div>
                  {fieldErrors.department && <span className="field-error">{fieldErrors.department}</span>}
                </div>

                <div className="input-group">
                  <label htmlFor="role_name">Role Requested</label>
                  <div className="input-with-icon">
                    <span className="input-icon">🛡</span>
                    <select
                      id="role_name"
                      name="role_name"
                      value={formData.role_name}
                      onChange={handleRoleChange}
                      className="role-select"
                    >
                      {ROLE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {lockedFieldConfig && (
                  <div className="input-group">
                    <label htmlFor={lockedFieldConfig.field}>{lockedFieldConfig.label}</label>
                    <div className="input-with-icon">
                      <span className="input-icon">{lockedFieldConfig.field === 'locked_brand' ? '🏷' : '🛒'}</span>
                      <select
                        id={lockedFieldConfig.field}
                        name={lockedFieldConfig.field}
                        value={formData[lockedFieldConfig.field]}
                        onChange={handleChange}
                        className={`role-select ${fieldErrors[lockedFieldConfig.field] ? 'input-error' : ''}`}
                        disabled={dimensionLoading}
                        required
                      >
                        <option value="">{dimensionLoading ? 'Loading options...' : lockedFieldConfig.placeholder}</option>
                        {lockedFieldOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>
                    <small className="field-hint">{lockedFieldConfig.helper}</small>
                    {fieldErrors[lockedFieldConfig.field] && (
                      <span className="field-error">{fieldErrors[lockedFieldConfig.field]}</span>
                    )}
                  </div>
                )}

                <div className="input-group">
                  <label htmlFor="password">Password</label>
                  <div className="input-with-icon">
                    <span className="input-icon">🔒</span>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Min 8 chars, upper, lower, number"
                      className={fieldErrors.password ? 'input-error' : ''}
                      required
                    />
                  </div>
                  {fieldErrors.password && <span className="field-error">{fieldErrors.password}</span>}
                </div>

                <div className="input-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <div className="input-with-icon">
                    <span className="input-icon">🔒</span>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Re-enter your password"
                      className={fieldErrors.confirmPassword ? 'input-error' : ''}
                      required
                    />
                  </div>
                  {fieldErrors.confirmPassword && <span className="field-error">{fieldErrors.confirmPassword}</span>}
                </div>

                <button 
                  type="submit" 
                  className={`btn-primary login-btn ${loading ? 'loading' : ''}`}
                  disabled={loading}
                >
                  {loading ? (
                    <span className="spinner"></span>
                  ) : (
                    <>
                      <span>Submit Registration</span>
                      <span className="btn-icon">→</span>
                    </>
                  )}
                </button>
              </form>
            )}

            <div className="auth-footer-link">
              Already have an account? <Link to="/login">Sign in here</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupWindow;
